import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EMPLEADO_DATALIST_NAME } from '../../../../model/contants/datalists';
import { UdeuService } from '../../../../services/udeu.service';
import { AppStore, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { RecordActionTypes } from '../../../../store/actions/record-actions';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Empleado, GaudeEmpleado } from '../../../../model/classes/gaude-empleado';
import { UdeuRecipient } from '../../../../model/classes/udeu-transfer-calendar';

@Component({
  selector: 'alden-recipients',
  templateUrl: './recipients.component.html',
  styleUrls: ['./recipients.component.scss'],
})
export class RecipientsComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() udeuRecipients: UdeuRecipient[];
    
  recipients: Empleado[] = [];
  options: Empleado[] = [];

  loading: boolean;
  disabled: boolean;

  private _formBuilder = inject(FormBuilder);
  formGroup: FormGroup<any>;

  RecordActionTypes: typeof RecordActionTypes = RecordActionTypes;

  constructor(private udeuService: UdeuService, protected store: Store<AppStore>) { }

  ngOnInit(): void {
    this.disabled =this.group.get(this.controlName).disabled;
    this.formGroup = this.createForm();

    if (this.udeuRecipients) {
      this.udeuRecipients.forEach((udeuRecipient) => {
        const form = this.createForm(udeuRecipient);
        this.disabled || udeuRecipient.gaudeId ? form.disable() : null;
        this.formArray.push(form);
      });
    }
  }

  createForm(recipient?: UdeuRecipient): FormGroup<any> {
    return this._formBuilder.group({
      search: ['', [Validators.minLength(3)]],
      gaudeId: [{ value: recipient?.gaudeId, disabled: true }],
      unit: [recipient?.unit, { value: recipient?.unit, disabled: true }],
      name: [recipient?.name, [Validators.required]],
      email: [recipient?.email, [Validators.required, Validators.email]],
      toNotify: [ recipient?.toNotify ?? false],    // TODO: PONER COMO OBLIGATORIO
    });
  }

  async search() {
    const value = this.formGroup.get('search').value;
    if (typeof value === 'string' && value.length >= 3) {
      this.loading = true;
      this.autocompleteTrigger.closePanel();
      try {
        const results: GaudeEmpleado = await this.udeuService.getGaudeDataListValues(EMPLEADO_DATALIST_NAME, value);
        this.options = results.items;
        if (this.options?.length === 1) {
          const employee = this.options[0];
          this.formGroup.get('search').setValue(employee);
          this.addGaudeRecipient(employee);
        } else {
          this.autocompleteTrigger.openPanel();
        }
      } catch (err) {
        console.error(err);
        this.store.dispatch(new SnackbarWarningAction('No se pudo obtener los destinatarios.'));
      } finally {
        this.loading = false;
      }
    } else if (value.length < 3) {
      this.resetForm();
    }
  }

  displayFn(recipient: Empleado): string {
    return recipient ? `${recipient.nombre} - ${recipient.email_deusto}`: '';
  }

  isSelected(option: Empleado, controlName: string): boolean {
    const value = this.formGroup.get(controlName).value;
    return value && typeof option !== 'string' ? value === option : false;
  }

  add() {
    const recipient = new UdeuRecipient();
    recipient.gaudeId = this.formGroup.get('gaudeId').value;
    recipient.name = this.formGroup.get('name').value?.trim();
    recipient.email = this.formGroup.get('email').value?.trim();
    recipient.unit = this.formGroup.get('unit').value?.trim();
    recipient.toNotify = this.formGroup.get('toNotify').value ?? false;

    const form = this.createForm(recipient);
    recipient.gaudeId ? form.disable() : null;
    this.formArray.push(form);
    this.resetForm();
  }
  
  addGaudeRecipient(recipient: Empleado) {
    this.formGroup.disable();
    this.formGroup.get('search').enable();

    this.formGroup.get('gaudeId').setValue(recipient.prc_cod);
    this.formGroup.get('name').setValue(recipient.nombre);
    this.formGroup.get('email').setValue(recipient.email_deusto);
    this.formGroup.get('unit').setValue(recipient.unidad);
    this.formGroup.get('toNotify').setValue(false);
  }

  resetForm() {
    this.options = [];
    this.formGroup.enable();
    this.formGroup.get('gaudeId').disable();

    this.formGroup.reset();
    this.formGroup.setErrors(null);
    this.formGroup.markAsUntouched();
  }

  delete(index: number) {
    const formArray = this.formArray;
    formArray.removeAt(index);
  }

  get formArray(): FormArray {
    return this.group.get(this.controlName) as FormArray;
  }

  formArrayGroup (index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

}
